import { lazy, Suspense, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { colors, radii } from "../styles/theme";
import { track } from "../lib/analytics";
import { usePageMeta } from "../lib/pageMeta";
import { getCapabilityTier } from "../lib/modelTier";

const LandingBelowFold = lazy(() => import("./LandingBelowFold"));

const LANDING_CSS = `
.lp-container   { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
.lp-section     { padding: 80px 0; }
.lp-hero-btns   { display: flex; justify-content: center; gap: 14px; flex-wrap: wrap; }
.lp-features-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-top: 48px; }
.lp-steps-grid    { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top: 48px; }
.lp-pricing-grid  { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; margin-top: 48px; }
.lp-feature-card  { background: var(--brand-bg-elevated); border: 1px solid var(--brand-border-strong); border-radius: 12px; padding: 28px; transition: box-shadow 0.2s ease, border-color 0.2s ease; cursor: default; }
.lp-feature-card:hover { border-color: rgba(247,220,111,0.4); box-shadow: 0 0 20px rgba(247,220,111,0.07); }
@media (max-width: 640px) {
  .lp-section     { padding: 52px 0; }
  .lp-features-grid { grid-template-columns: 1fr; }
  .lp-steps-grid    { grid-template-columns: 1fr; }
  .lp-pricing-grid  { grid-template-columns: 1fr; }
  .lp-hero-btns   { flex-direction: column; align-items: center; }
  .lp-term        { display: none; }
}
.lp-term { display: block; }
.lp-try-btn {
  padding: 14px 32px;
  min-height: 52px;
  background: linear-gradient(135deg, var(--brand-status-success), var(--brand-primary));
  color: var(--brand-bg-deep);
  border: none;
  border-radius: 6px;
  font-weight: 800;
  font-size: 1rem;
  cursor: pointer;
  font-family: inherit;
  letter-spacing: 0.02em;
  box-shadow: 0 0 24px rgba(76,175,136,0.25);
  transition: all 0.15s ease;
}
.lp-try-btn:hover { background: linear-gradient(135deg, color-mix(in srgb, var(--brand-status-success) 80%, white), var(--brand-status-success)); transform: translateY(-1px); box-shadow: 0 0 36px rgba(76,175,136,0.35); }
.lp-steps-strip { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; margin-top: 52px; }
@media (max-width: 640px) { .lp-steps-strip { grid-template-columns: 1fr; } }
`;

const AGENTS = [
  { name: "backend",  model: "sonnet", status: "busy", detail: "deploying Lambda v2…",         color: colors.goldSecondary },
  { name: "frontend", model: "sonnet", status: "idle", detail: "awaiting next job",             color: colors.green },
  { name: "legal",    model: "sonnet", status: "busy", detail: "drafting compliance memo…",     color: colors.goldSecondary },
  { name: "patents",  model: "opus",   status: "idle", detail: "PCT review complete",            color: colors.green },
  { name: "ceo",      model: "opus",   status: "busy", detail: "orchestrating 9 sessions…",    color: colors.goldSecondary },
];

// 3-step value prop strip beneath the hero — what you can do in 3 seconds of reading
const VALUE_STEPS = [
  {
    icon: "⚡",
    heading: "Persistent agents, not chats",
    body: "Your fleet keeps running 24/7. Pull it up anytime — full context intact from days ago.",
  },
  {
    icon: "🤝",
    heading: "Agents talk to each other",
    body: "CEO dispatches, backend ships, legal reviews — they coordinate without you lifting a finger.",
  },
  {
    icon: "🔌",
    heading: "Connects to your stack",
    body: "GitHub, Gmail, Cloudflare, Stripe. Agents act on your behalf with OAuth-brokered credentials.",
  },
];

function TerminalMockup() {
  return (
    <div className="lp-term" style={{ margin: "52px auto 0", maxWidth: 560, background: colors.bgHeader, border: `1px solid ${colors.borderSubtle}`, borderRadius: 10, padding: "16px 20px", fontFamily: "monospace", fontSize: "0.78rem", color: colors.textMuted, boxShadow: "0 0 40px rgba(247,220,111,0.07)", textAlign: "left" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14 }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: colors.error, display: "inline-block" }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: colors.warning, display: "inline-block" }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: colors.green, display: "inline-block" }} />
        <span style={{ marginLeft: 8, color: colors.borderDefault, fontSize: "0.72rem" }}>2hives.ai · agent board</span>
      </div>
      {AGENTS.map(a => (
        <div key={a.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0", borderBottom: "1px solid rgba(42,74,58,0.5)" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: a.color, flexShrink: 0 }} />
          <span style={{ width: 72, color: colors.textSubtle, fontWeight: 700 }}>{a.name}</span>
          <span style={{ width: 52, color: colors.borderDefault }}>[{getCapabilityTier(a.model)}]</span>
          <span style={{ width: 36, color: a.color }}>{a.status}</span>
          <span style={{ color: colors.borderDefault, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.detail}</span>
        </div>
      ))}
      <div style={{ marginTop: 10, color: colors.borderSubtle, fontSize: "0.7rem" }}>
        5 agents · 3 busy · 2 idle · uptime 11d 4h
      </div>
    </div>
  );
}

export function FleetLanding() {
  const { token } = useAuth();
  const navigate  = useNavigate();
  const brand     = useBrand();

  // #2455 — per-page SEO meta (og:image + twitter card)
  usePageMeta({
    title: "2hives.ai — Background AI agents, running 24/7 on your projects",
    description: "Spin up a fleet of always-on AI agents. They watch your repos, monitor your inbox, run your scripts, and tag-team work across projects.",
    ogTitle: "2hives.ai — Background AI agents, running 24/7 on your projects",
    ogDescription: "Spin up a fleet of always-on AI agents. They watch your repos, monitor your inbox, run your scripts, and tag-team work across projects.",
    twitterTitle: "2hives.ai — Background AI agents, running 24/7",
    twitterDescription: "Persistent agents per project. Always-on, cross-session, pay only what you use.",
    canonical: "https://2hives.ai/",
  });

  // Note: when signed in, the root route ("/") renders RootDashboard via
  // RootEntry in App.tsx — this component is only mounted for signed-out
  // visitors. We keep a defensive guard in case it gets reached via another
  // path (e.g. /signup → / forward) while a token is still in localStorage.
  useEffect(() => {
    if (token) navigate("/", { replace: true });
  }, [token, navigate]);

  // #2446 — track landing view once per mount (signed-out visitors only)
  useEffect(() => {
    if (!token) track("landing_view");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (token) return null;

  return (
    <>
      <style>{LANDING_CSS}</style>
      <div style={{ background: colors.bgDeep, color: colors.textBody, fontFamily: "'Inter', system-ui, sans-serif", overflowX: "hidden" }}>

        {/* ── Sticky nav ── */}
        <nav aria-label="Main navigation" style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(13,31,20,0.97)", backdropFilter: "blur(8px)", borderBottom: `1px solid ${colors.borderSubtle}`, height: 56 }}>
          <div className="lp-container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img src={brand.logo_url} alt="2hives.ai logo" width={28} height={28} />
              <span style={{ fontWeight: 700, fontSize: "1rem", color: colors.goldPrimary, letterSpacing: "0.04em" }}>
                2hives.ai
              </span>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => navigate("/signup")}
                aria-label="Sign up for free"
                style={{ padding: "8px 18px", background: `linear-gradient(135deg, ${colors.goldPrimary}, ${colors.goldSecondary})`, color: colors.bgDeep, border: "none", borderRadius: radii.sm, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", fontFamily: "inherit", minHeight: 36 }}
              >
                Get started free
              </button>
              <button
                onClick={() => navigate("/login")}
                aria-label="Sign in"
                style={{ padding: "8px 18px", background: "none", border: `1px solid ${colors.borderDefault}`, color: colors.textMuted, borderRadius: radii.sm, fontSize: "0.85rem", cursor: "pointer", fontFamily: "inherit", minHeight: 36 }}
              >
                Sign in
              </button>
            </div>
          </div>
        </nav>

        {/* ── Hero ── */}
        <section aria-labelledby="hero-heading" style={{ padding: "96px 0 64px" }}>
          <div className="lp-container" style={{ textAlign: "center" }}>
            {/* Beta badge */}
            <div style={{ display: "inline-block", padding: "5px 14px", background: "rgba(247,220,111,0.1)", border: "1px solid rgba(247,220,111,0.25)", borderRadius: radii.pill, fontSize: "0.73rem", color: colors.goldPrimary, fontWeight: 600, letterSpacing: "0.07em", marginBottom: 22, textTransform: "uppercase" }}>
              Now in beta
            </div>

            {/* H1 — tight 3-second value proposition */}
            <h1 id="hero-heading" style={{ margin: "0 0 20px", fontWeight: 800, lineHeight: 1.13, letterSpacing: "-0.02em", color: colors.textBody }}>
              <span style={{ display: "block", fontSize: "clamp(1.05rem, 2.5vw, 1.3rem)", color: colors.goldPrimary, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 12 }}>
                2hives.ai
              </span>
              <span style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)" }}>
                Your projects never sleep —<br />
                <span style={{ background: `linear-gradient(135deg, ${colors.goldPrimary}, ${colors.goldSecondary})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  Claude agents run them 24/7
                </span>
              </span>
            </h1>

            <p style={{ margin: "0 auto 36px", maxWidth: 580, fontSize: "clamp(0.95rem, 2vw, 1.1rem)", color: colors.textMuted, lineHeight: 1.7 }}>
              Deploy a fleet of always-on AI agents. They ship code, triage your inbox,
              and collaborate across projects while you sleep.
              No session limits. No context loss.
            </p>

            {/* Primary CTA: Try — no signup required */}
            <div className="lp-hero-btns">
              {/* #2315 — primary CTA is now try-without-signup, deep-linking
                  to the demo hive (#2317). Removes the signup wall as the
                  first ask; signup becomes the secondary action. */}
              <button
                onClick={() => navigate("/try")}
                className="lp-try-btn"
                aria-label="Try 2hives.ai in browser — no signup required"
              >
                Try in browser — no signup
              </button>
              <button
                onClick={() => navigate("/signup")}
                style={{ padding: "14px 32px", minHeight: 52, background: `linear-gradient(135deg, ${colors.goldPrimary}, ${colors.goldSecondary})`, color: colors.bgDeep, border: "none", borderRadius: radii.sm, fontWeight: 700, fontSize: "1rem", cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em" }}
              >
                Get started free
              </button>
              <button
                onClick={() => navigate("/login")}
                style={{ padding: "14px 32px", minHeight: 52, background: "none", border: `1px solid ${colors.borderDefault}`, color: colors.textMuted, borderRadius: radii.sm, fontSize: "1rem", cursor: "pointer", fontFamily: "inherit" }}
              >
                Sign in
              </button>
            </div>

            {/* 3-step value prop strip */}
            <div className="lp-steps-strip" style={{ marginTop: 52, textAlign: "left" }}>
              {VALUE_STEPS.map(({ icon, heading, body }, i) => (
                <div
                  key={i}
                  style={{
                    padding: "24px 28px",
                    borderTop: `1px solid ${colors.borderSubtle}`,
                    borderRight: i < 2 ? `1px solid ${colors.borderSubtle}` : "none",
                  }}
                >
                  <div style={{ fontSize: "1.6rem", marginBottom: 10 }}>{icon}</div>
                  <div style={{ fontWeight: 700, fontSize: "0.95rem", color: colors.textBody, marginBottom: 6 }}>{heading}</div>
                  <div style={{ fontSize: "0.85rem", color: colors.textMuted, lineHeight: 1.65 }}>{body}</div>
                </div>
              ))}
            </div>

            <TerminalMockup />
            <p className="lp-term" style={{ marginTop: 14, fontSize: "0.78rem", color: "var(--brand-border-strong)" }}>
              Live preview — {" "}
              <button
                onClick={() => navigate("/try")}
                style={{ background: "none", border: "none", color: colors.goldPrimary, cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", textDecoration: "underline", padding: 0 }}
              >
                open the interactive demo
              </button>
            </p>
          </div>
        </section>

        {/* ── Below fold (lazy) ── */}
        <Suspense fallback={<div style={{ minHeight: 600, background: colors.bgDeep }} />}>
          <LandingBelowFold onSignup={() => navigate("/signup")} onLogin={() => navigate("/login")} onTry={() => navigate("/try")} />
        </Suspense>

      </div>
    </>
  );
}
